import { onMounted, onUnmounted, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { syncService } from '@/services/syncService.js'
import { useAuth } from '@/composables/auth/useAuth.js'

export const SYNC_COMPLETED_EVENT = 'sourcing:sync-completed'

const DEFAULT_POLL_INTERVAL = 15_000

// Query keys whose data is derived from each synchronized data domain. Query
// key prefixes intentionally invalidate all filter/date variants of a query.
const QUERY_KEYS_BY_SYNC_TYPE = {
  hours: [
    ['employees'],
    ['weekly-availability'],
    ['employee-activities'],
    ['hours-stats'],
    ['assign-availability']
  ],
  employees: [
    ['employees'],
    ['teams'],
    ['aanspreekpunten'],
    ['filter-options'],
    ['employees-filter-counts'],
    ['weekly-availability'],
    ['employee-activities'],
    ['assign-availability']
  ],
  'employees-all': [
    ['employees'],
    ['teams'],
    ['aanspreekpunten'],
    ['filter-options'],
    ['employees-filter-counts'],
    ['weekly-availability'],
    ['employee-activities'],
    ['assign-availability']
  ],
  prospects: [['prospects-leads'], ['prospect'], ['nav-badges']],
  'prospects-historical': [['prospects-leads'], ['prospect'], ['nav-badges']],
  'timeline-historical': [['prospects-leads'], ['prospect'], ['nav-badges']],
  'timeline-prospects': [['prospects-leads'], ['prospect'], ['nav-badges']],
  all: [
    ['employees'],
    ['teams'],
    ['aanspreekpunten'],
    ['filter-options'],
    ['employees-filter-counts'],
    ['weekly-availability'],
    ['employee-activities'],
    ['hours-stats'],
    ['assign-availability'],
    ['prospects-leads'],
    ['prospect'],
    ['nav-badges']
  ]
}

// A local sync already refreshes the initiating page through this event. The
// marker prevents the global poller in that same tab from processing the same
// completion a second time when it observes the updated last-synced value.
const locallyPublishedSyncTypes = new Set()

export function invalidateQueriesForSync(queryClient, syncType) {
  const queryKeys = new Set([['last-synced'], ...(QUERY_KEYS_BY_SYNC_TYPE[syncType] || [])])

  return Promise.all([...queryKeys].map(queryKey => queryClient.invalidateQueries({ queryKey })))
}

export function publishSyncCompleted(syncType) {
  locallyPublishedSyncTypes.add(syncType)

  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent(SYNC_COMPLETED_EVENT, {
      detail: { syncType, source: 'local' }
    })
  )
}

/**
 * Listen for successful syncs from every client. The sync endpoint is polled,
 * not the larger data endpoints, so pages only refetch when their data is
 * known to have changed.
 */
export function useSyncRefresh({ pollInterval = DEFAULT_POLL_INTERVAL } = {}) {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuth()

  const lastSeenSyncs = new Map()
  let pollId = null
  let requestInFlight = false
  let generation = 0

  const announceSyncCompletion = (syncType, completedAt, source = 'poll') => {
    if (typeof window === 'undefined') return

    window.dispatchEvent(
      new CustomEvent(SYNC_COMPLETED_EVENT, {
        detail: { syncType, completedAt, source }
      })
    )
  }

  const handleSyncCompleted = event => {
    const syncType = event.detail?.syncType
    if (!syncType) return

    invalidateQueriesForSync(queryClient, syncType).catch(() => {
      // A refresh failure should not affect the rest of the application.
    })
  }

  const pollLastSynced = async () => {
    if (!isAuthenticated.value || document.hidden || requestInFlight) return

    const requestGeneration = generation
    requestInFlight = true

    try {
      const timestamps = await syncService.getLastSynced()
      if (requestGeneration !== generation) return

      for (const [syncType, timestamp] of Object.entries(timestamps || {})) {
        if (!timestamp) continue

        const previous = lastSeenSyncs.get(syncType)
        lastSeenSyncs.set(syncType, timestamp)

        // Establish a baseline when this tab starts; opening a page should not
        // be treated as a new sync event. Clear a marker from a local sync that
        // completed before the first baseline was available.
        if (!previous) {
          locallyPublishedSyncTypes.delete(syncType)
          continue
        }

        const previousMs = new Date(previous).getTime()
        const currentMs = new Date(timestamp).getTime()
        if (!Number.isFinite(currentMs) || currentMs <= previousMs) continue

        // The initiating tab already received the local completion event.
        if (locallyPublishedSyncTypes.delete(syncType)) continue

        announceSyncCompletion(syncType, timestamp)
      }
    } catch {
      // A temporary status endpoint failure is harmless; the next poll retries.
    } finally {
      requestInFlight = false
    }
  }

  const stopPolling = (resetSeen = false) => {
    generation++
    if (pollId) {
      clearInterval(pollId)
      pollId = null
    }
    requestInFlight = false
    // Keep the previous snapshot while hidden so a sync completed in the
    // background is announced when the tab becomes visible again.
    if (resetSeen) lastSeenSyncs.clear()
  }

  const startPolling = () => {
    if (pollId || !isAuthenticated.value || document.hidden) return

    pollLastSynced()
    pollId = setInterval(pollLastSynced, pollInterval)
  }

  const handleVisibilityChange = () => {
    if (document.hidden) {
      stopPolling()
    } else if (isAuthenticated.value) {
      startPolling()
    }
  }

  onMounted(() => {
    window.addEventListener(SYNC_COMPLETED_EVENT, handleSyncCompleted)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    startPolling()
  })

  onUnmounted(() => {
    window.removeEventListener(SYNC_COMPLETED_EVENT, handleSyncCompleted)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    stopPolling(true)
  })

  watch(
    isAuthenticated,
    authenticated => {
      if (authenticated) {
        startPolling()
      } else {
        stopPolling(true)
      }
    },
    { immediate: true }
  )

  return { refreshSyncStatus: pollLastSynced }
}

export default useSyncRefresh
